// ---- the animals ----
// How an animal is made and how it moves: the template models, the clone that
// stands in the field, the pose drawn each frame from the clips that came in
// the file, the step every brain shares, and the moves every brain makes.
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {clone as cloneSkinned} from 'three/addons/utils/SkeletonUtils.js';
import {asset} from '../../core/assets';
import {ANIMALS,type AnimalSpec} from './data';
import {addCollider,fitHeight,footprint,isSkinned,posed} from '../../core/body';
import {scene} from '../../core/scene';
import {onWater} from '../../core/water';
import {rnd,turnToward} from '../../core/util';
import type {Animal,Brain} from './types';

export const tpl:Record<string,THREE.Object3D>={}, tplClips:Record<string,THREE.AnimationClip[]>={};
// every model on the roster, fetched once; a round is only dealt for one that has arrived
export function loadModels(){
  for(const a of ANIMALS)new GLTFLoader().loadAsync(asset(a.file)).then(g=>{tpl[a.w]=g.scene;tplClips[a.w]=g.animations||[];}).catch(err=>console.warn(a.file+' did not load',err));
}
export function makeAnimal(spec:AnimalSpec,brain:Brain):Animal{
  const a=new THREE.Group() as Animal, m=cloneSkinned(tpl[spec.w]), anims=tplClips[spec.w]||[];
  if(!anims.length)console.warn(spec.w+': no clips came with it; it will stand still');
  // A clip is authored in the model's own frame, so the model is left exactly
  // as the file has it: the pack faces +Z, which is the way the field turns it.
  posed(m); const sz=fitHeight(m,spec.h);
  m.traverse(o=>{if(isSkinned(o)){o.castShadow=true;o.frustumCulled=false;o.normalizeSkinWeights();o.userData.ele=a;}});
  a.add(m);
  a.pad=footprint(a);                            // how far its feet reach: the pond's edge grows by this much for it
  // A bought model is rigged for clips, not for joint angles: the Quirky
  // elephant is a single `body` hub with one bone per leg — no knee, no neck,
  // no head bone at all. Nothing here reads a skeleton; the clips that came
  // in the file are what drive it (clipPose).
  Object.assign(a,{w:spec.w,spec,swims:spec.swims,clips:anims,mixer:anims.length?new THREE.AnimationMixer(m):null,act:null,actName:'',
    cols:[],full:1,brain,
    phase:0,walk:0,hopT:-1e9,glanceT:0,glanceEnd:-1e9,glanceClip:'Idle_B',st:'pause',stride:1,
    stT:0,wait:0,stMs:0,born:0,tx:0,tz:0,hx0:0,hz0:0,hx1:0,hz1:0});
  // Its collision body: three spheres down its own length, sized from the
  // model's box. There are no joints worth hanging them on one by one — a
  // raised trunk carries no sphere with it — but it is enough that two
  // elephants push apart and neither walks through Jiddo.
  const br=Math.min(sz.x,sz.y*0.7)*0.5;
  for(const f of [-0.28,0,0.28])addCollider(a,0,sz.y*0.55,f*sz.z,br);
  a.stride=sz.z*CLIPSTRIDE;                      // one walk cycle carries it this far
  scene.add(a); return a;
}
// ---- the moves every brain makes ----
// A state is three things set at once — which, since when, and for how long —
// and a brain that set two of them is how a hushed sheep once fell into a rest
// with no length to it and froze the game. So they are set together or not at
// all: a pause thinks again after lo..hi ms, a state entered lasts ms, and a
// walk (or a swim, or a dash) sets off for s.
export const pause=(a:Animal,t:number,lo:number,hi:number)=>{a.st='pause';a.stT=t;a.wait=rnd(lo,hi);};
export const float=(a:Animal,t:number,lo:number,hi:number)=>{a.st='float';a.stT=t;a.wait=rnd(lo,hi);};   // a swimmer's pause, on the water
export const enter=(a:Animal,st:State,t:number,ms:number)=>{a.st=st;a.stT=t;a.stMs=ms;};
export const walkTo=(a:Animal,s:{x:number;z:number},t:number,st:State='walk')=>{a.tx=s.x;a.tz=s.z;a.st=st;a.stT=t;};
// shared by every brain: a step toward (tx,tz); true while still walking
export function stepToward(a:Animal,t:number,dt:number){
  const p=a.position, dx=a.tx-p.x, dz=a.tz-p.z, d=Math.hypot(dx,dz);
  if(d<0.25||t-a.stT>10000){pause(a,t,500,1600);return false;}
  a.head=Math.atan2(dx,dz);
  const v=Math.min(d,a.spec.speed*(a.slow||1)*dt), x=p.x+dx/d*v, z=p.z+dz/d*v;
  if(!a.spec.swims&&onWater(x,z,a.pad)){pause(a,t,500,1600);return false;}   // the bank: stop, think again
  p.x=x; p.z=z;
  a.phase+=v/a.stride*2*Math.PI;                // legs cycle with the ground covered, so feet never skate
  return true;
}
// ---- the clips ----
// A walk's cadence is tied to ground covered, not to the clock, so that a
// planted foot never skates: a clip played on a timer skates exactly the way a
// sine-driven hip would. So the walk clip is not played on a timer at all; its
// time is set from the distance walked. CLIPSTRIDE is how far one cycle carries
// the animal, as a fraction of its own body length, and is the one number in
// here that was found by eye (Jiddo's strides are measured off his clips
// instead: clipStride, core/humanoid.ts).
const CLIPSTRIDE=0.55;
// Which clip each state of the brains asks for. Trumpet, bray, moo and crow
// are all "the animal makes its noise and everyone looks": the
// recorded cry still fires on entering the state, and Clicked is what plays
// under it — a crouch, then the animal rearing up on its hind legs with its
// head thrown high, the elephant's trunk with it; the rooster stands up tall
// with his wings out. It used to be Attack, which on every quadruped in the
// pack is a head-down charge — dropped head, rear kicked up, a shove forward —
// and in a crowd, with the noise over it, that read as the animal going for
// whoever it had just bumped. The duck's quack is Hit, her head back and her
// wings flung up; on her, Attack is a peck. The snake's hiss keeps Attack,
// which on her is the rear-up and the strike, tongue out. (clipsheet.html draws
// every clip of a model in a grid; that is how these were chosen.) The cow's
// rest is the pack's Sit clip, the one pose in the box that is a whole state
// by itself. The duck's swim and float are both the Swim clip: she paddles
// whether she is going somewhere or not. The owl's flight is the Fly clip on
// the clock, flapping the whole way, with the arc written under it (brains.ts);
// her hoot is Clicked like the other cries. The fox's stalk is the Walk clip
// slowed, still timed by the ground; his pounce is Jump with an arc written
// under it. A state is a key here — a brain that invents one without a clip
// under it does not type-check — and the clip has to be in the model too: the
// models carry only the clips their brains can reach (scripts/strip-clips.js),
// and scripts/check-clips.js fails the build when a brain can ask for one that
// its animal's file was stripped of.
export const CLIPOF={walk:'Walk',pause:'Idle_A',trumpet:'Clicked',drink:'Eat',graze:'Eat',bray:'Clicked',
  hop:'Jump',land:'Idle_A',eat:'Eat',moo:'Clicked',rest:'Sit',swim:'Swim',float:'Swim',quack:'Hit',
  hiss:'Attack',coil:'Sit',startle:'Fear',hide:'Sit',crow:'Clicked',peck:'Eat',flap:'Fly',
  fly:'Fly',hoot:'Clicked',stalk:'Walk',pounce:'Jump',sniff:'Eat',yip:'Clicked',curl:'Sit',
  cry:'Clicked',dash:'Run',follow:'Walk',jump:'Jump',pronk:'Jump'} as const;
export type State=keyof typeof CLIPOF;
// The cries. Every pack clip is one 0.42 s loop that starts and ends at rest,
// and a recorded bray runs 1.7 s: played on the clock, that is four rears in a
// row. So a cry's clip is not played on the clock either — its time is set from
// the state's age, one cycle stretched over the recording, the way the walk's
// is set from the ground. The crouch fills the first quarter of the noise, the
// rear sits on the loudest part, and it is back on its feet as the sound ends.
const CRIES:ReadonlySet<State>=new Set<State>(['trumpet','bray','moo','quack','hiss','crow','hoot','yip','cry']);
// The walks: timed by the ground stepToward() covers, never by the clock, so a planted foot never skates.
const GROUND:ReadonlySet<State>=new Set<State>(['walk','stalk','dash','follow']);
// Three of the eighteen clips are idles. Idle_A is the breathing every pause
// plays. Idle_B and Idle_C are a look to the left and a look to the right — the
// whole body hub turns, the head being part of it — each one 0.42 s glance
// that starts and ends where the breath's loop does. So a glance is not a
// state a brain has to know about: it pauses, and the pose now and then cuts
// from the breathing to one look and back, with no brain asking. It starts as
// a breath ends, so the two
// clips meet at the same pose and the blend between them can be short; a
// pause that ends mid-glance fades into the walk like any other change of clip.
const IDLES=['Idle_A','Idle_B','Idle_C'], GLANCE_GAP=[1500,5000];
function clipPose(a:Animal,mixer:THREE.AnimationMixer,t:number,dt:number){
  let want:string=CLIPOF[a.st]||'Idle_A';
  if(a.st==='pause'){
    if(t<a.glanceEnd)want=a.glanceClip;
    else if(t>a.glanceT&&a.act&&a.actName==='Idle_A'&&a.act.time+dt>=a.act.getClip().duration){
      a.glanceClip=IDLES[1+(Math.random()*2|0)];             // left or right
      const c=a.clips.find(c=>c.name===a.glanceClip);
      if(c){a.glanceEnd=t+c.duration*1000;a.glanceT=a.glanceEnd+rnd(GLANCE_GAP[0],GLANCE_GAP[1]);want=a.glanceClip;}
      else a.glanceT=Infinity;                                 // a file without the looks never glances
    }
  }
  if(a.actName!==want){
    const next=mixer.clipAction(a.clips.find(c=>c.name===want)||a.clips[0]);
    next.reset().play();
    if(a.act&&a.act!==next)a.act.crossFadeTo(next,IDLES.includes(want)&&IDLES.includes(a.actName)?0.1:0.25,false);
    a.act=next; a.actName=want;
  }
  const act=a.act!, d=act.getClip().duration;
  if(GROUND.has(a.st)){act.paused=true; act.time=((a.phase/(2*Math.PI)*d)%d+d)%d;}                   // timed by the ground, not by the clock
  else if(CRIES.has(a.st)){act.paused=true; act.time=Math.min(d-1e-3,d*(t-a.stT)/Math.max(1,a.stMs));}   // one cycle, over the recording
  else act.paused=false;
  mixer.update(dt);
}
// the end of every brain's frame: face the way it is going, draw the pose, set it on the ground
export function settle(a:Animal,t:number,dt:number,walking:boolean){
  if(a.head!==undefined)a.rotation.y=turnToward(a.rotation.y,a.head,3*dt);   // the whole animal, by the shortest arc
  a.walk+=((walking?1:0)-a.walk)*Math.min(1,6*dt);            // the bob settles in rather than snapping
  if(a.mixer)clipPose(a,a.mixer,t,dt);
  const hk=Math.min(1,(t-a.hopT)/380);
  a.position.y=a.walk*0.03*Math.abs(Math.sin(a.phase))+0.25*Math.sin(Math.PI*hk)+(a.hopY||0)+(a.dip||0);
}
